import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Copy, Download, ExternalLink, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import TotalsPanel from "@/components/quote/TotalsPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { getApiErrorMessage } from "@/lib/apiError";

function LineSection({ title, items }) {
    if (!items || items.length === 0) return null;
    return (
        <Card className="rounded-xl border-slate-200 p-5 space-y-3">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((it, i) => (
                        <TableRow key={i}>
                            <TableCell>{it.description}</TableCell>
                            <TableCell className="text-right tabular">{it.qty}</TableCell>
                            <TableCell className="text-right tabular">{money(it.unit_price)}</TableCell>
                            <TableCell className="text-right tabular text-slate-500">{money(it.cost || 0)}</TableCell>
                            <TableCell className="text-right tabular font-medium">{money((it.qty || 0) * (it.unit_price || 0))}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

export default function QuoteView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [shareToken, setShareToken] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = () => api.get(`/quotes/${id}`).then((r) => {
        setQuote(r.data);
        setShareToken(r.data.share_token || null);
    });
    useEffect(() => { load(); }, [id]);

    if (!quote) return <div className="text-slate-500">Loading…</div>;

    const customer = quote.customer_snapshot || {};
    const home = quote.home || {};
    const totals = quote.totals || {};

    const duplicate = async () => {
        const r = await api.post(`/quotes/${id}/duplicate`);
        toast.success("Quote duplicated");
        navigate(`/quotes/${r.data.id}/edit`);
    };
    const enableShare = async () => {
        const r = await api.post(`/quotes/${id}/share`);
        setShareToken(r.data.share_token);
        toast.success("Share link enabled");
    };
    const copyShare = () => {
        navigator.clipboard.writeText(`${window.location.origin}/quote/${shareToken}`);
        toast.success("Share link copied");
    };
    const downloadPdf = async () => {
        const r = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${quote.quote_number}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
    };
    const setStatus = async (s) => {
        const r = await api.post(`/quotes/${id}/status`, { status: s });
        setQuote((prev) => ({ ...prev, ...r.data }));
        toast.success(`Marked ${s}`);
    };
    const deleteQuote = async () => {
        const customerName = `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "this customer";
        const confirmed = window.confirm(
            `Delete quote ${quote.quote_number} for ${customerName}?\n\nThis cannot be undone. If this quote was shared, the public link will stop working.`
        );
        if (!confirmed) return;

        setDeleting(true);
        try {
            await api.delete(`/quotes/${id}`);
            toast.success("Quote deleted");
            navigate("/quotes");
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Unable to delete quote"));
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="quote-view-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <Link to="/quotes" className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                        <ArrowLeft className="h-3 w-3" /> All quotes
                    </Link>
                    <div className="flex items-center gap-3 mt-1">
                        <h1 className="font-display text-3xl text-slate-900">{customer.first_name} {customer.last_name}</h1>
                        <StatusBadge status={quote.status} />
                    </div>
                    <div className="text-xs text-slate-500 font-mono mt-1">{quote.quote_number}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Select value={quote.status} onValueChange={setStatus}>
                        <SelectTrigger className="w-[160px]" data-testid="quote-view-status-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => navigate(`/quotes/${id}/edit`)} data-testid="quote-view-edit-button">
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                    </Button>
                    <Button variant="outline" onClick={duplicate} data-testid="quote-view-duplicate-button">
                        <Copy className="mr-2 h-4 w-4" /> Duplicate
                    </Button>
                    <Button onClick={downloadPdf} className="bg-[#0F2A47] hover:bg-[#14385E] text-white" data-testid="quote-view-download-pdf-button">
                        <Download className="mr-2 h-4 w-4" /> PDF
                    </Button>
                    {!shareToken ? (
                        <Button variant="outline" onClick={enableShare} data-testid="quote-view-enable-share-button">
                            <ExternalLink className="mr-2 h-4 w-4" /> Enable share
                        </Button>
                    ) : (
                        <Button variant="outline" onClick={copyShare} data-testid="quote-view-copy-share-button">
                            <Copy className="mr-2 h-4 w-4" /> Copy share link
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={deleteQuote}
                        disabled={deleting}
                        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                        data-testid="quote-view-delete-button"
                    >
                        <Trash2 className="mr-2 h-4 w-4" /> {deleting ? "Deleting…" : "Delete"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
                <div className="space-y-4 min-w-0">
                    <Tabs defaultValue="summary">
                        <TabsList>
                            <TabsTrigger value="summary" data-testid="quote-view-tab-summary">Summary</TabsTrigger>
                            <TabsTrigger value="line-items" data-testid="quote-view-tab-items">Line items</TabsTrigger>
                            <TabsTrigger value="internal" data-testid="quote-view-tab-internal">Internal</TabsTrigger>
                        </TabsList>
                        <TabsContent value="summary" className="space-y-4 mt-4">
                            <Card className="rounded-xl border-slate-200 p-5 space-y-3">
                                <div className="text-sm font-semibold text-slate-900">Customer</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-6 text-sm">
                                    <div><div className="text-xs text-slate-500">Name</div>{customer.first_name} {customer.last_name}</div>
                                    <div><div className="text-xs text-slate-500">Phone</div>{customer.phone || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Email</div>{customer.email || "—"}</div>
                                    <div className="col-span-2 md:col-span-3"><div className="text-xs text-slate-500">Address</div>
                                        {[customer.current_address, customer.current_city, customer.current_state, customer.current_zip].filter(Boolean).join(", ") || "—"}
                                    </div>
                                </div>
                            </Card>
                            <Card className="rounded-xl border-slate-200 p-5 space-y-3">
                                <div className="text-sm font-semibold text-slate-900">Home</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-6 text-sm">
                                    <div><div className="text-xs text-slate-500">Manufacturer</div>{home.manufacturer || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Series</div>{home.series || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Model</div>{home.model_name || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Type</div>{home.home_type || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Beds / Baths</div>{home.beds || "—"} / {home.baths || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Sqft</div>{home.sqft || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Dimensions</div>{home.dimensions || "—"}</div>
                                    <div><div className="text-xs text-slate-500">Wind zone</div>{home.wind_zone || "—"}</div>
                                </div>
                            </Card>
                            {(quote.notes_customer || quote.next_steps || quote.disclaimer) && (
                                <Card className="rounded-xl border-slate-200 p-5 space-y-3">
                                    {quote.notes_customer && (
                                        <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Customer notes</div>
                                            <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{quote.notes_customer}</div>
                                        </div>
                                    )}
                                    {quote.next_steps && (
                                        <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-500">Next steps</div>
                                            <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{quote.next_steps}</div>
                                        </div>
                                    )}
                                </Card>
                            )}
                        </TabsContent>
                        <TabsContent value="line-items" className="space-y-4 mt-4">
                            <Card className="rounded-xl border-slate-200 p-5">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-semibold text-slate-900">Base home price</div>
                                    <div className="font-medium tabular">{money(quote.base_price)}</div>
                                </div>
                            </Card>
                            <LineSection title="Options & upgrades" items={quote.options} />
                            <LineSection title="Site Work" items={quote.mandatory_services} />
                            <LineSection title="Add-ons" items={quote.addons} />
                            <LineSection title="Delivery" items={quote.site_work} />
                            {quote.discounts?.length > 0 && (
                                <Card className="rounded-xl border-slate-200 p-5 space-y-2">
                                    <div className="text-sm font-semibold text-slate-900">Discounts</div>
                                    {quote.discounts.map((d, i) => (
                                        <div key={i} className="flex justify-between text-sm">
                                            <span>{d.description}</span>
                                            <span className="tabular">- {money(d.amount)}</span>
                                        </div>
                                    ))}
                                </Card>
                            )}
                        </TabsContent>
                        <TabsContent value="internal" className="space-y-4 mt-4">
                            <Card className="rounded-xl border-slate-200 p-5 space-y-2">
                                <div className="text-sm font-semibold text-slate-900">Profit & commission</div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-6 text-sm tabular">
                                    <div><div className="text-xs text-slate-500">Factory cost</div>{money(totals.factory_cost || 0)}</div>
                                    <div><div className="text-xs text-slate-500">EHS price (calc)</div>{money(totals.ehs_price_calculated || 0)}</div>
                                    <div><div className="text-xs text-slate-500">MSRP (calc)</div>{money(totals.msrp_calculated || 0)}</div>
                                    <div><div className="text-xs text-slate-500">Gross margin</div>{money(totals.gross_margin || 0)}</div>
                                    <div><div className="text-xs text-slate-500">Admin fee (5%)</div>{money(totals.admin_fee || 0)}</div>
                                    <div><div className="text-xs text-slate-500">Loan fee</div>{money(totals.loan_fee || 0)}</div>
                                    <div><div className="text-xs text-slate-500">Agent commission</div>{money(totals.agent_commission || 0)}</div>
                                    <div><div className="text-xs text-slate-500">Net take home</div><span className={totals.target_met ? "text-[#167A4A]" : "text-amber-700"}>{money(totals.net_take_home || 0)}</span></div>
                                    <div><div className="text-xs text-slate-500">Services profit</div>{money(totals.total_services_profit || 0)}</div>
                                </div>
                            </Card>
                            {quote.notes_internal && (
                                <Card className="rounded-xl border-slate-200 p-5">
                                    <div className="text-xs uppercase tracking-wide text-slate-500">Internal notes</div>
                                    <div className="text-sm whitespace-pre-wrap mt-2">{quote.notes_internal}</div>
                                </Card>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
                <div>
                    <TotalsPanel totals={totals} />
                </div>
            </div>
            {shareToken && (
                <Card className="rounded-xl border-slate-200 p-4 flex items-center justify-between">
                    <div className="text-sm text-slate-700">
                        Customer-facing share link enabled.
                        <a href={`/quote/${shareToken}`} target="_blank" rel="noreferrer" className="ml-2 text-[#1E6FA8] hover:underline">
                            Open public view
                        </a>
                    </div>
                    <div className="text-xs text-slate-500 break-all">{window.location.origin}/quote/{shareToken}</div>
                </Card>
            )}
        </div>
    );
}
